import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Icon } from '@/components/Icon'
import { fetchPlanRevision } from '@/features/planning/api'
import type { ApiError } from '@/lib/api/client'
import type { PlanRevision } from '@/types/planning'

/**
 * One committed version of a plan, openable.
 *
 * The list of revisions was already shown; what was missing was the ability to
 * read one — which is the only reason to keep a revision at all. A committed
 * plan is the thing a quarter gets judged against, so "what did we actually
 * agree in July" has to be answerable.
 */
export function RevisionRow({ planId, revision }: { planId: string; revision: PlanRevision }) {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)

  const detail = useQuery<unknown, ApiError>({
    queryKey: ['plan-revision', planId, revision.id],
    queryFn: () => fetchPlanRevision(planId, revision.id),
    enabled: open,
    retry: false,
  })

  return (
    <li className="px-4 py-2.5">
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        className="flex w-full items-center gap-2 text-start"
      >
        <Icon name="chevron" className={`text-ink-500 size-3 shrink-0 ${open ? 'rotate-90' : ''}`} />
        <span className="text-ink-900 min-w-0 flex-1">{revision.reason}</span>
        <span className="text-ink-500 font-mono text-xs">
          {revision.author ?? '—'} · {revision.recordedAt?.slice(0, 10) ?? '—'}
        </span>
      </button>

      {open ? (
        detail.data === undefined ? (
          <p className="text-ink-500 mt-2 font-mono text-[11px]">{t('common.loading')}</p>
        ) : (
          <pre className="border-line bg-surface-2 text-ink-700 mt-2 overflow-x-auto rounded border p-2 font-mono text-[11px]">
            {JSON.stringify(detail.data, null, 2)}
          </pre>
        )
      ) : null}
    </li>
  )
}
